import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { attempt, backend, toast, useApp } from '../data/store';
import { BEST_TIMES, HELP_HOURS, MAX_SCREENSHOTS, bestTimeLabel, canRequestCall, lastPhone, phoneProblem, screenshotProblem, slotsByDay, splitCalls, type BestTime, type CallAllowance, type SupportCall, type SupportCallback, type SupportMessage, type SupportTicket } from '../data/support';
import { Icon } from '../ui/icons';
import { Segmented, confirm } from '../ui/controls';
import { openSheet } from '../app/ui';

/**
 * Settings › Support Centre: one page with a selector at the top. New ticket (with screenshots), Book a call
 * (Basic: 2 a month, Pro: unlimited), Request a call (Basic and Pro: Noor MS rings back), your Tickets (each opens its
 * conversation here), your Calls (video calls and call backs, with Noor MS's notes), and (Pro) Remote access for
 * Noor MS. The iPhone app has the same page.
 */

type View = 'ticket' | 'book' | 'callme' | 'tickets' | 'calls' | 'remote';
const STATUS: Record<SupportTicket['status'], [string, string]> = { open: ['Waiting for Noor MS', 'idle'], answered: ['Noor MS replied', 'online'], closed: ['Closed', 'off'] };

export function SupportTab() {
  const ws = useApp((s) => s.ws)!;
  const session = useApp((s) => s.session);
  const tz = ws.masjid.timezone;
  const api = backend.support;
  // Opens on Tickets when there are active ones (waiting for Noor MS, or replied), otherwise on New ticket.
  // Manage on the dashboard's "Remote access is live" card opens straight on Remote access.
  const [view, setView] = useState<View | null>(() => (new URLSearchParams(location.search).get('view') === 'remote' ? 'remote' : null));
  const [tickets, setTickets] = useState<SupportTicket[] | null>(null);
  const [calls, setCalls] = useState<SupportCall[] | null>(null);
  const [backs, setBacks] = useState<SupportCallback[] | null>(null);
  const [help, setHelp] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const load = useCallback(async () => {
    try {
      const [t, c, h, k] = await Promise.all([api.tickets(), api.calls(), api.help(), api.callbacks().catch(() => [] as SupportCallback[])]);
      setTickets(t); setCalls(c); setHelp(h); setBacks(k); setError('');
      setView((v) => v ?? (k.some((x) => x.unseen) ? 'calls' : t.some((x) => x.status !== 'closed') ? 'tickets' : 'ticket'));
    } catch (e) { setError((e as Error).message); setView((v) => v ?? 'ticket'); }
  }, [api]);
  // On a phone the tabs scroll sideways: keep the chosen one in view.
  useEffect(() => { document.querySelector('.sup-tabs button[aria-pressed="true"]')?.scrollIntoView({ block: 'nearest', inline: 'nearest' }); }, [view]);
  useEffect(() => {
    void load();
    const again = () => { if (document.visibilityState === 'visible') void load(); };
    document.addEventListener('visibilitychange', again);
    const t = setInterval(again, 60_000);
    return () => { document.removeEventListener('visibilitychange', again); clearInterval(t); };
  }, [load]);
  const when = useCallback((iso: string, o: Intl.DateTimeFormatOptions = { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' }) => new Date(iso).toLocaleString('en-GB', { timeZone: tz, ...o }), [tz]);
  const { upcoming } = splitCalls(calls ?? []);
  const replied = (tickets ?? []).filter((t) => t.status === 'answered').length;
  const waiting = (backs ?? []).find((k) => k.status === 'requested') ?? null;
  const unseen = (backs ?? []).filter((k) => k.unseen).length;
  // Looking at Calls clears the badge for done call backs (they stay marked New until the page is left).
  useEffect(() => { if (view === 'calls' && unseen > 0) void api.callbacksSeen().catch(() => {}); }, [view, unseen, api]);
  const plan = ws.masjid.plan;
  const count = (n: number) => n > 0 ? <span className="seg-count">{n}</span> : null;

  return (
    <div className="support">
      <header className="sup-head">
        <span className="sup-mark"><Icon name="headphones" size={26} /></span>
        <div className="sup-title">
          <h2>Support Centre</h2>
          <p>Help from the Noor MS team. Replies and calls show here and in your notifications.</p>
        </div>
        <span className={`sup-plan plan-${plan}`}>{plan === 'pro' ? 'Pro' : plan === 'basic' ? 'Basic' : 'Free'} plan</span>
      </header>

      <Segmented<View> label="Support Centre" className="sup-tabs" value={view ?? 'ticket'} onChange={(v) => { setView(v); setOpenId(null); }} options={[
        { value: 'ticket', label: <><Icon name="plus" size={16} />New ticket</> },
        { value: 'book', label: <><Icon name="calendar" size={16} />Book a call</> },
        { value: 'callme', label: <><Icon name="call" size={16} />Request a call</> },
        { value: 'tickets', label: <>Tickets{count(replied)}</> },
        { value: 'calls', label: <>Calls{count(upcoming.length + (waiting ? 1 : 0) + (view === 'calls' ? 0 : unseen))}</> },
        { value: 'remote', label: <>Remote access{help ? <span className="seg-dot" aria-label="on" /> : null}</> },
      ]} />

      {error && <div className="banner bad">{error}</div>}

      <div className="sup-panel" key={view ?? 'loading'}>
        {view === null && <section className="card glass sup-card"><p className="hint">Loading…</p></section>}
        {view === 'ticket' && <NewTicket onDone={async (id) => { await load(); setView('tickets'); setOpenId(id); }} />}
        {view === 'book' && <BookCall tz={tz} onBooked={async () => { await load(); setView('calls'); }} />}
        {view === 'callme' && <RequestCall plan={plan} backs={backs} waiting={waiting} when={when} onChanged={load} />}
        {view === 'tickets' && (openId
          ? <Thread id={openId} ticket={(tickets ?? []).find((t) => t.id === openId)} tz={tz} onBack={() => { setOpenId(null); void load(); }} onChanged={load} />
          : <TicketList tickets={tickets} when={when} onOpen={setOpenId} onNew={() => setView('ticket')} />)}
        {view === 'calls' && <CallList calls={calls} backs={backs} when={when} onBook={() => setView('book')} onChanged={load} />}
        {view === 'remote' && <Remote plan={plan} help={help} canHelp={session?.role === 'owner' || session?.role === 'admin'} when={when} onChange={setHelp} />}
      </div>
    </div>
  );
}

type When = (iso: string, o?: Intl.DateTimeFormatOptions) => string;

/* ---------- New ticket ---------- */

function Shots({ files, onChange }: { files: File[]; onChange: (f: File[]) => void }) {
  const input = useRef<HTMLInputElement>(null);
  const urls = useMemo(() => files.map((f) => URL.createObjectURL(f)), [files]);
  useEffect(() => () => urls.forEach((u) => URL.revokeObjectURL(u)), [urls]);
  const [over, setOver] = useState(false);
  const add = (list: FileList | null) => {
    if (!list) return;
    const next = [...files];
    for (const f of Array.from(list)) {
      const p = screenshotProblem(f);
      if (p) { toast(p, { bad: true }); continue; }
      if (next.length >= MAX_SCREENSHOTS) { toast(`Attach up to ${MAX_SCREENSHOTS} screenshots.`, { bad: true }); break; }
      next.push(f);
    }
    onChange(next);
  };
  return (
    <div className={`sup-drop${over ? ' over' : ''}`} onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={(e) => { e.preventDefault(); setOver(false); add(e.dataTransfer.files); }}>
      {files.map((f, i) => (
        <span key={i} className="shot"><img src={urls[i]} alt={f.name} /><button type="button" className="lbtn" aria-label={`Remove ${f.name}`} onClick={() => onChange(files.filter((_, j) => j !== i))}><Icon name="x" size={14} /></button></span>
      ))}
      {files.length < MAX_SCREENSHOTS && (
        <button type="button" className="sup-add" onClick={() => input.current?.click()}>
          <Icon name="image" size={20} />
          <span><b>Add screenshots</b><small>Drop them here, or choose. Up to {MAX_SCREENSHOTS}, 10 MB each.</small></span>
        </button>
      )}
      <input ref={input} type="file" accept="image/*" multiple hidden onChange={(e) => { add(e.target.files); e.target.value = ''; }} />
    </div>
  );
}

function NewTicket({ onDone }: { onDone: (id: string) => void }) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const ok = title.trim().length >= 3 && body.trim().length > 0;
  const send = async () => {
    setBusy(true);
    const id = await attempt(() => backend.support.openTicket(title, body, files), 'Ticket sent. Noor MS will reply here.');
    setBusy(false);
    if (id !== undefined) { setTitle(''); setBody(''); setFiles([]); onDone(id as string); }
  };
  return (
    <section className="card glass sup-card">
      <div className="sup-card-head"><h3>New ticket</h3><p>Tell us what’s wrong or what you need. We usually reply within a working day.</p></div>
      <div className="sup-form">
        <label className="sup-field"><span>Title</span>
          <input value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. The hall screen shows a black picture" />
        </label>
        <label className="sup-field"><span>Details</span>
          <textarea value={body} maxLength={5000} rows={6} onChange={(e) => setBody(e.target.value)} placeholder="What happened, on which screen, and when?" />
        </label>
        <Shots files={files} onChange={setFiles} />
      </div>
      <div className="sup-actions"><span className="hint">{ok ? 'Ready to send' : 'Add a title and the details'}</span><span className="spacer" />
        <button type="button" className="btn primary" disabled={!ok || busy} onClick={send}><Icon name="send" size={18} />{busy ? 'Sending…' : 'Send ticket'}</button></div>
    </section>
  );
}

/* ---------- Book a call ---------- */

function BookCall({ tz, onBooked }: { tz: string; onBooked: () => void }) {
  const [allow, setAllow] = useState<CallAllowance | null>(null);
  const [slots, setSlots] = useState<string[] | null>(null);
  const [day, setDay] = useState<string | null>(null);
  const [time, setTime] = useState<string | null>(null);
  const [topic, setTopic] = useState('');
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try {
      const a = await backend.support.allowance();
      setAllow(a);
      if (a.plan !== 'free') setSlots(await backend.support.slots());
    } catch (e) { toast((e as Error).message, { bad: true }); }
  }, []);
  useEffect(() => { void load(); }, [load]);
  // Basic's 2 calls are per calendar month: the count follows the month of the time chosen.
  const [forTime, setForTime] = useState<CallAllowance | null>(null);
  useEffect(() => {
    if (!time) { setForTime(null); return; }
    let live = true;
    void backend.support.allowance(time).then((a) => { if (live) setForTime(a); }).catch(() => {});
    return () => { live = false; };
  }, [time, allow]);
  const days = useMemo(() => slotsByDay(slots ?? [], tz), [slots, tz]);
  const chosen = days.find((d) => d.day === day) ?? days[0];
  const fmt = (iso: string, o: Intl.DateTimeFormatOptions) => new Date(iso).toLocaleString('en-GB', { timeZone: tz, ...o });
  const book = async () => {
    if (!time) return;
    setBusy(true);
    const ok = await attempt(() => backend.support.book(time, topic), `Call booked for ${fmt(time, { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })}`);
    setBusy(false);
    if (ok !== undefined) { setTime(null); setTopic(''); onBooked(); }
  };
  const shown = forTime ?? allow;
  const left = shown?.limit != null ? Math.max(0, shown.limit - shown.used) : null;
  const monthName = time ? fmt(time, { month: 'long' }) : 'this month';

  if (!allow) return <section className="card glass sup-card"><p className="hint">Loading…</p></section>;
  if (allow.plan === 'free') return (
    <section className="card glass sup-card sup-hero">
      <span className="sup-hero-ic"><Icon name="video" size={30} /></span>
      <h3>Talk it through with Noor MS</h3>
      <p>A 30-minute video call: share your screen and we’ll walk you through it, or fix it with you. Calls come with Basic (2 a month) and Pro (unlimited).</p>
      <p className="hint">On Free, raise a ticket and we’ll reply.</p>
      <button type="button" className="btn primary" onClick={() => openSheet({ name: 'upgrade' })}>See plans</button>
    </section>
  );
  return (
    <section className="card glass sup-card">
      <div className="sup-card-head">
        <h3>Book a call</h3>
        <p>A 30-minute Google Meet call with Noor MS. You can share your screen.</p>
        <span className="sup-allow">{shown?.limit == null ? 'Unlimited on Pro' : `${left} of ${shown.limit} left ${time ? `in ${monthName}` : 'this month'}`}</span>
      </div>
      {!slots ? <p className="hint">Finding free times…</p> : !days.length ? <p className="hint">No free times in the coming weeks. Raise a ticket and we’ll get back to you.</p> : (<>
        <div className="sup-step"><b>1</b><span>Choose a day</span></div>
        <div className="chips" role="radiogroup" aria-label="Day">
          {days.slice(0, 21).map((d) => {
            const on = (chosen?.day ?? '') === d.day;
            const date = new Date(`${d.day}T12:00:00Z`);
            return <button key={d.day} type="button" role="radio" aria-checked={on} className={`chip day${on ? ' on' : ''}`} onClick={() => { setDay(d.day); setTime(null); }}>
              <small>{date.toLocaleDateString('en-GB', { weekday: 'short', timeZone: 'UTC' })}</small><b>{date.getUTCDate()}</b><small>{date.toLocaleDateString('en-GB', { month: 'short', timeZone: 'UTC' })}</small>
            </button>;
          })}
        </div>
        {chosen && <>
          <div className="sup-step"><b>2</b><span>Choose a time <small className="hint">({chosen.times.length} free)</small></span></div>
          <div className="sup-times" role="radiogroup" aria-label="Time">
            {chosen.times.map((t) => <button key={t} type="button" role="radio" aria-checked={time === t} className={`chip${time === t ? ' on' : ''}`} onClick={() => setTime(t)}>{fmt(t, { hour: '2-digit', minute: '2-digit' })}</button>)}
          </div>
        </>}
        <div className="sup-step"><b>3</b><span>What’s it about? <small className="hint">(optional)</small></span></div>
        <input className="sup-input" value={topic} maxLength={500} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. Setting up the prayer board" aria-label="What’s it about?" />
        {left === 0 && <p className="hint" style={{ margin: 0 }}>Your {shown?.limit} calls for {monthName} are booked. Choose a day in another month, or <button type="button" className="link" onClick={() => openSheet({ name: 'upgrade' })}>upgrade to Pro</button> for unlimited calls.</p>}
        <div className="sup-actions"><span className="sup-summary">{time ? <><Icon name="calendar" size={16} />{fmt(time, { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })}</> : <span className="hint">Choose a time</span>}</span><span className="spacer" />
          <button type="button" className="btn primary" disabled={!time || busy || left === 0} onClick={book}>{busy ? 'Booking…' : 'Book call'}</button></div>
      </>)}
    </section>
  );
}

/* ---------- Request a call ---------- */

/** The open call back: what Noor MS will ring about, and Cancel. */
function Waiting({ k, when, onChanged }: { k: SupportCallback; when: When; onChanged: () => void }) {
  return (
    <section className="card glass sup-card sup-call">
      <span className="sup-hero-ic sm"><Icon name="callIn" size={24} /></span>
      <div className="sup-main">
        <b>Call requested</b>
        <small>{k.topic}</small>
        <small>{k.phone} · {bestTimeLabel(k.bestTime)} · asked {when(k.createdAt, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</small>
      </div>
      <div className="sup-call-actions">
        <span className="pill idle">Waiting</span>
        <button type="button" className="btn plain" onClick={async () => {
          if (!(await confirm('Cancel your call request?', { body: k.topic, ok: 'Cancel request', danger: true }))) return;
          if (await attempt(() => backend.support.cancelCallback(k.id), 'Call request cancelled')) onChanged();
        }}>Cancel</button>
      </div>
    </section>
  );
}

function RequestCall({ plan, backs, waiting, when, onChanged }: { plan: string; backs: SupportCallback[] | null; waiting: SupportCallback | null; when: When; onChanged: () => void }) {
  const [phone, setPhone] = useState('');
  const [topic, setTopic] = useState('');
  const [best, setBest] = useState<BestTime>('any');
  const [busy, setBusy] = useState(false);
  const filled = useRef(false);
  useEffect(() => { if (backs && !filled.current) { filled.current = true; setPhone(lastPhone(backs)); } }, [backs]);
  if (!canRequestCall(plan)) return (
    <section className="card glass sup-card sup-hero">
      <span className="sup-hero-ic"><Icon name="call" size={28} /></span>
      <h3>Request a call</h3>
      <p>Leave your number and Noor MS will ring you back. Call backs come with Basic and Pro.</p>
      <button type="button" className="btn primary" onClick={() => openSheet({ name: 'upgrade' })}>See plans</button>
    </section>
  );
  if (!backs) return <section className="card glass sup-card"><p className="hint">Loading…</p></section>;
  if (waiting) return <Waiting k={waiting} when={when} onChanged={onChanged} />;
  const bad = phone.trim() ? phoneProblem(phone) : null;
  const ok = !phoneProblem(phone) && topic.trim().length >= 3;
  const send = async () => {
    setBusy(true);
    const id = await attempt(() => backend.support.requestCall(phone, topic, best), 'Call requested. Noor MS will ring you back.');
    setBusy(false);
    if (id !== undefined) { setTopic(''); onChanged(); }
  };
  return (
    <section className="card glass sup-card">
      <div className="sup-card-head"><h3>Request a call</h3><p>Noor MS will ring you back on this number.</p></div>
      <div className="sup-form">
        <label className="sup-field"><span>Phone number</span>
          <input type="tel" inputMode="tel" autoComplete="tel" value={phone} maxLength={24} onChange={(e) => setPhone(e.target.value)} placeholder="e.g. 07700 900123" aria-invalid={!!bad} />
          {bad && <small className="sup-bad">{bad}</small>}
        </label>
        <label className="sup-field"><span>What’s it about?</span>
          <textarea value={topic} maxLength={500} rows={3} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. The hall screen shows the wrong prayer times" />
        </label>
        <div className="sup-field"><span id="sup-best">Best time to call</span>
          <div className="sup-best" role="radiogroup" aria-labelledby="sup-best">
            {BEST_TIMES.map(([k, l]) => <button key={k} type="button" role="radio" aria-checked={best === k} className={`chip${best === k ? ' on' : ''}`} onClick={() => setBest(k)}>{l}</button>)}
          </div>
        </div>
      </div>
      <div className="sup-actions"><span className="hint">{ok ? 'Ready to send' : 'Add your number and what it’s about'}</span><span className="spacer" />
        <button type="button" className="btn primary" disabled={!ok || busy} onClick={send}><Icon name="call" size={18} />{busy ? 'Sending…' : 'Request call'}</button></div>
    </section>
  );
}

/* ---------- Tickets ---------- */

function TicketList({ tickets, when, onOpen, onNew }: { tickets: SupportTicket[] | null; when: When; onOpen: (id: string) => void; onNew: () => void }) {
  if (!tickets) return <section className="card glass sup-card"><p className="hint">Loading…</p></section>;
  if (!tickets.length) return (
    <section className="card glass sup-card sup-hero">
      <span className="sup-hero-ic"><Icon name="send" size={28} /></span>
      <h3>No tickets yet</h3>
      <p>Raise one and Noor MS will reply here.</p>
      <button type="button" className="btn primary" onClick={onNew}><Icon name="plus" size={18} />New ticket</button>
    </section>
  );
  const row = (t: SupportTicket) => (
    <button key={t.id} type="button" className="sup-item" onClick={() => onOpen(t.id)}>
      <span className={`sup-ic ${t.status}`}><Icon name={t.status === 'closed' ? 'check' : 'send'} size={18} /></span>
      <span className="sup-main"><b>{t.title}</b><small>{when(t.updatedAt, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })} · {t.by}</small></span>
      <span className={`pill ${STATUS[t.status][1]}`}>{STATUS[t.status][0]}</span>
      <Icon name="chevron" size={18} className="chev" />
    </button>
  );
  const active = tickets.filter((t) => t.status !== 'closed'), closed = tickets.filter((t) => t.status === 'closed');
  return (<>
    {active.length > 0 && <section className="card glass sup-card flush"><div className="sup-list-title">Active</div>{active.map(row)}</section>}
    {closed.length > 0 && <section className="card glass sup-card flush"><div className="sup-list-title">Closed</div>{closed.map(row)}</section>}
  </>);
}

function Shot({ path }: { path: string }) {
  const [url, setUrl] = useState('');
  useEffect(() => { let live = true; void backend.support.imageUrl(path).then((u) => { if (live) setUrl(u); }).catch(() => {}); return () => { live = false; }; }, [path]);
  return url ? <a className="shot" href={url} target="_blank" rel="noopener noreferrer"><img src={url} alt="Screenshot" /></a> : <span className="shot loading" />;
}

function Thread({ id, ticket, tz, onBack, onChanged }: { id: string; ticket?: SupportTicket; tz: string; onBack: () => void; onChanged: () => void }) {
  const [msgs, setMsgs] = useState<SupportMessage[] | null>(null);
  const [body, setBody] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(ticket?.status ?? 'open');
  useEffect(() => { if (ticket) setStatus(ticket.status); }, [ticket]);
  const end = useRef<HTMLDivElement>(null);
  const load = useCallback(async () => { try { setMsgs(await backend.support.messages(id)); } catch (e) { toast((e as Error).message, { bad: true }); } }, [id]);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => { end.current?.scrollIntoView({ block: 'nearest' }); }, [msgs]);
  const send = async () => {
    setBusy(true);
    const ok = await attempt(() => backend.support.reply(id, body, files), 'Sent to Noor MS');
    setBusy(false);
    if (ok !== undefined) { setBody(''); setFiles([]); setStatus('open'); void load(); onChanged(); }
  };
  return (
    <section className="card glass sup-card">
      <div className="sup-thread-head">
        <button type="button" className="btn sm plain" onClick={onBack}><Icon name="back" size={16} />All tickets</button>
        <span className="spacer" />
        {status !== 'closed' && <button type="button" className="btn sm plain" onClick={async () => { if (await attempt(() => backend.support.closeTicket(id), 'Ticket closed')) { setStatus('closed'); onChanged(); } }}>Close ticket</button>}
      </div>
      <div className="sup-card-head"><h3>{ticket?.title ?? 'Ticket'}</h3><span className={`pill ${STATUS[status][1]}`}>{STATUS[status][0]}</span></div>
      <div className="thread">
        {!msgs ? <p className="hint">Loading…</p> : msgs.map((m) => (
          <div key={m.id} className={`msg${m.fromNoorMS ? ' noorms' : ''}`}>
            <small>{m.fromNoorMS ? 'Noor MS' : m.author} · {new Date(m.at).toLocaleString('en-GB', { timeZone: tz, day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</small>
            <p>{m.body}</p>
            {m.images.length > 0 && <div className="shots">{m.images.map((p) => <Shot key={p} path={p} />)}</div>}
          </div>
        ))}
        <div ref={end} />
      </div>
      <div className="sup-composer">
        <textarea value={body} maxLength={5000} rows={3} onChange={(e) => setBody(e.target.value)} placeholder={status === 'closed' ? 'Write again to re-open this ticket' : 'Write a reply'} aria-label="Reply" />
        <Shots files={files} onChange={setFiles} />
        <div className="sup-actions"><span className="spacer" />
          <button type="button" className="btn primary" disabled={busy || !body.trim()} onClick={send}><Icon name="send" size={18} />{busy ? 'Sending…' : status === 'closed' ? 'Re-open and send' : 'Send'}</button></div>
      </div>
    </section>
  );
}

/* ---------- Calls ---------- */

/** A done or cancelled call back, in Earlier. */
function CallbackRow({ k, when }: { k: SupportCallback; when: When }) {
  const done = k.status === 'done' && k.calledAt;
  return (
    <div className={`sup-item cb${done ? '' : ' dim'}`}>
      <span className={`sup-ic${done ? ' answered' : ''}`}><Icon name="call" size={18} /></span>
      <span className="sup-main">
        <span className="sup-cb-head">
          <b>{done ? `Call took place ${when(k.calledAt!, { weekday: 'long', day: 'numeric', month: 'long' })} at ${when(k.calledAt!, { hour: '2-digit', minute: '2-digit' })}` : k.cancelledBy === 'noorms' ? 'Call request closed by Noor MS' : 'Call request cancelled'}</b>
          {k.unseen && <span className="pill online">New</span>}
        </span>
        <small>Regarding {k.topic}</small>
        {k.outcome && <span className="sup-note">{k.outcome}</span>}
      </span>
    </div>
  );
}

function CallList({ calls, backs, when, onBook, onChanged }: { calls: SupportCall[] | null; backs: SupportCallback[] | null; when: When; onBook: () => void; onChanged: () => void }) {
  if (!calls) return <section className="card glass sup-card"><p className="hint">Loading…</p></section>;
  const { upcoming, past } = splitCalls(calls);
  const waiting = (backs ?? []).find((k) => k.status === 'requested');
  // Earlier: past video calls and finished call backs together, latest first.
  const earlier = [
    ...past.map((c) => ({ at: c.startsAt, c, k: null as SupportCallback | null })),
    ...(backs ?? []).filter((k) => k.status !== 'requested').map((k) => ({ at: k.calledAt ?? k.createdAt, c: null as SupportCall | null, k })),
  ].sort((a, b) => b.at.localeCompare(a.at));
  return (<>
    {waiting && <Waiting k={waiting} when={when} onChanged={onChanged} />}
    {upcoming.length ? upcoming.map((c) => (
      <section key={c.id} className="card glass sup-card sup-call">
        <span className="sup-date"><small>{when(c.startsAt, { month: 'short' })}</small><b>{when(c.startsAt, { day: 'numeric' })}</b></span>
        <div className="sup-main">
          <b>{when(c.startsAt, { weekday: 'long', hour: '2-digit', minute: '2-digit' })}–{when(c.endsAt, { hour: '2-digit', minute: '2-digit' })}</b>
          <small>30-minute video call{c.topic ? ` · ${c.topic}` : ''} · booked by {c.by}</small>
          {!c.meetUrl && <small className="hint">The Google Meet link appears here before the call.</small>}
        </div>
        <div className="sup-call-actions">
          {c.meetUrl && <a className="btn primary" href={c.meetUrl} target="_blank" rel="noopener noreferrer"><Icon name="external" size={16} />Join call</a>}
          <button type="button" className="btn plain" onClick={async () => {
            if (!(await confirm('Cancel this call?', { body: `${when(c.startsAt)} with Noor MS.`, ok: 'Cancel call', danger: true }))) return;
            if (await attempt(() => backend.support.cancel(c.id), 'Call cancelled')) onChanged();
          }}>Cancel</button>
        </div>
      </section>
    )) : waiting ? null : (
      <section className="card glass sup-card sup-hero">
        <span className="sup-hero-ic"><Icon name="calendar" size={28} /></span>
        <h3>No calls coming up</h3>
        <p>Book a 30-minute video call and it will show here with its Google Meet link.</p>
        <button type="button" className="btn primary" onClick={onBook}>Book a call</button>
      </section>
    )}
    {earlier.length > 0 && (
      <section className="card glass sup-card flush">
        <div className="sup-list-title">Earlier</div>
        {earlier.slice(0, 12).map(({ c, k }) => k ? <CallbackRow key={k.id} k={k} when={when} /> : (
          <div key={c!.id} className="sup-item dim">
            <span className="sup-ic"><Icon name="video" size={18} /></span>
            <span className="sup-main"><b>{when(c!.startsAt)}</b><small>{c!.status === 'cancelled' ? (c!.cancelledBy === 'noorms' ? 'Cancelled by Noor MS' : 'Cancelled') : 'Done'}{c!.topic ? ` · ${c!.topic}` : ''}</small></span>
          </div>
        ))}
      </section>
    )}
  </>);
}

/* ---------- Remote access ---------- */

function Remote({ plan, help, canHelp, when, onChange }: { plan: string; help: string | null; canHelp: boolean; when: When; onChange: (h: string | null) => void }) {
  const [busy, setBusy] = useState(false);
  const set = async (h: 0 | 1 | 4 | 24) => {
    setBusy(true);
    const until = await attempt(() => backend.support.setHelp(h));
    setBusy(false);
    if (until !== undefined) { onChange(h ? (until as string) : null); toast(h ? `Remote access given for ${h} hour${h === 1 ? '' : 's'}` : 'Remote access switched off'); }
  };
  return (
    <section className="card glass sup-card">
      <div className="sup-remote">
        <span className={`sup-hero-ic${help ? ' on' : ''}`}><Icon name="monitor" size={28} /></span>
        <div>
          <h3>Give remote access</h3>
          <p>Let Noor MS open your NoorView and fix things for you during a call: settings, designs and screens. It switches itself off when the time is up, you can switch it off any time, and each visit is listed in your notifications.</p>
        </div>
      </div>
      {plan !== 'pro' ? (
        <div className="sup-upgrade"><span>Remote access is part of the Pro plan.</span><span className="spacer" /><button type="button" className="btn primary" onClick={() => openSheet({ name: 'upgrade' })}>Upgrade to Pro</button></div>
      ) : !canHelp ? <p className="hint" style={{ margin: 0 }}>Only the owner or an admin can give remote access.</p> : (<>
        <div className={`sup-status${help ? ' on' : ''}`}><span className="sup-led" />{help ? <>Noor MS has access until <b>{when(help, { hour: '2-digit', minute: '2-digit' })}</b></> : 'Off. Noor MS can’t open your NoorView.'}</div>
        <div className="sup-choices" role="group" aria-label="Give remote access for">
          {HELP_HOURS.map((h) => <button key={h} type="button" className="sup-choice" disabled={busy} onClick={() => set(h)}><b>{h}</b><small>{h === 1 ? 'hour' : 'hours'}</small></button>)}
        </div>
        {help && <button type="button" className="btn plain danger" disabled={busy} onClick={() => set(0)}>Switch off now</button>}
      </>)}
    </section>
  );
}
